import React, { useState } from "react";
import { FiEdit2, FiTrash2, FiCheck, FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import api from "@/services/api";

const translations = {
  en: {
    interview_note: "Interview note",
    author: "By {{name}} • {{date}}",
    add_note_placeholder: "Add a note...",
    add_note_button: "Add the note",
    saving: "Saving...",
    note_saved: "Note saved successfully",
    note_updated: "Note updated successfully",
    note_deleted: "Note deleted successfully",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    confirm_delete: "Are you sure you want to delete this note?",
    edited: "edited",
  },
  fr: {
    interview_note: "Note d'entretien",
    author: "Par {{name}} • {{date}}",
    add_note_placeholder: "Ajouter une note...",
    add_note_button: "Ajouter la note",
    saving: "Enregistrement...",
    note_saved: "Note enregistrée avec succès",
    note_updated: "Note mise à jour avec succès",
    note_deleted: "Note supprimée avec succès",
    edit: "Modifier",
    delete: "Supprimer",
    cancel: "Annuler",
    save: "Enregistrer",
    confirm_delete: "Êtes-vous sûr de vouloir supprimer cette note ?",
    edited: "modifié",
  },
};

export default function CommentsTab({ candidate, setCandidate }) {
  const { t } = useComponentTranslation(translations);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editContent, setEditContent] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;

    setIsSubmitting(true);
    try {
      const newNote = {
        content: note.trim(),
        created_at: new Date(),
      };
      
      const updatedNotes = [...(candidate.notes || []), newNote];
      const response = await api.put(`/user/${candidate._id}`, { notes: updatedNotes });

      if (!response.ok) return toast.error(response.data.message);
      toast.success(t("note_saved"));
      setNote("");
      setCandidate(response.data);
    } catch (e) {
      console.error("Error saving note:", e);
      toast.error("Error saving note");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (index, content) => {
    setEditingIndex(index);
    setEditContent(content);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditContent("");
  };

  const handleSaveEdit = async (index) => {
    if (!editContent.trim()) return;

    setIsSubmitting(true);
    try {
      const actualIndex = candidate.notes.length - 1 - index; // Reverse index
      const updatedNotes = [...candidate.notes];
      updatedNotes[actualIndex] = {
        ...updatedNotes[actualIndex],
        content: editContent.trim(),
        updated_at: new Date(),
      };
      
      const response = await api.put(`/user/${candidate._id}`, { notes: updatedNotes });

      if (!response.ok) return toast.error(response.data.message);
      toast.success(t("note_updated"));
      setEditingIndex(null);
      setEditContent("");
      setCandidate(response.data);
    } catch (e) {
      console.error("Error updating note:", e);
      toast.error("Error updating note");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (index) => {
    if (!confirm(t("confirm_delete"))) return;

    setIsSubmitting(true);
    try {
      const actualIndex = candidate.notes.length - 1 - index; // Reverse index
      const updatedNotes = candidate.notes.filter((_, i) => i !== actualIndex);
      
      const response = await api.put(`/user/${candidate._id}`, { notes: updatedNotes });

      if (!response.ok) return toast.error(response.data.message);
      toast.success(t("note_deleted"));
      setCandidate(response.data);
    } catch (e) {
      console.error("Error deleting note:", e);
      toast.error("Error deleting note");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleString("en", { month: "short" });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <div className="space-y-3">
      {/* Existing notes */}
      {candidate.notes && candidate.notes.length > 0 && (
        <>
          {[...candidate.notes].reverse().map((note, index) => (
            <div key={index} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                {note.user_name && note.created_at && (
                  <p className="text-xs text-gray-500">
                    {t("author", {
                      name: note.user_name,
                      date: formatDate(note.created_at),
                    })}
                    {note.updated_at && <span className="ml-1 italic">({t("edited")})</span>}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(index, note.content)}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    title={t("edit")}
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                    title={t("delete")}
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {editingIndex === index ? (
                <div className="space-y-2">
                  <textarea
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    rows="4"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSaveEdit(index)}
                      disabled={isSubmitting || !editContent.trim()}
                      className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiCheck className="w-3 h-3" />
                      <span>{t("save")}</span>
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={isSubmitting}
                      className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <FiX className="w-3 h-3" />
                      <span>{t("cancel")}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-700 leading-relaxed">{note.content}</p>
              )}
            </div>
          ))}
        </>
      )}

      {/* Add new note form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        <textarea
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          rows="4"
          placeholder={t("add_note_placeholder")}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          type="submit"
          disabled={isSubmitting || !note.trim()}
          className="btn btn-primary btn-small"
        >
          <span>{isSubmitting ? t("saving") : t("add_note_button")}</span>
        </button>
      </form>
    </div>
  );
}
